import React, { useState, useEffect, useRef } from 'react';
import { Star, Upload, Trash2, X, FileText } from 'lucide-react';
import { Report, ReportEvaluationFile } from '../../types';

interface EvaluationModalProps {
    report: Report | null;
    onClose: () => void;
    onSave: (updatedReport: Report) => Promise<void>;
}

const ModernEvaluationModal: React.FC<EvaluationModalProps> = ({ report, onClose, onSave }) => {
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState('');
    const [files, setFiles] = useState<ReportEvaluationFile[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (report) {
            setRating(report.evaluation?.rating || 0);
            setComment(report.evaluation?.comment || '');
            setFiles(report.evaluation?.files || []);
        }
    }, [report]);

    if (!report) return null;

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles: ReportEvaluationFile[] = Array.from(e.target.files).map((file: File) => ({
                id: `${file.name}-${Date.now()}`,
                file: file,
                fileName: file.name,
                url: '' // Will be populated by backend
            }));
            setFiles(prev => [...prev, ...newFiles]);
        }
    };

    const removeFile = (fileId: string) => {
        setFiles(prev => prev.filter(f => f.id !== fileId));
    };

    const handleSave = async () => {
        setIsSaving(true);
        const updatedReport: Report = {
            ...report,
            evaluation: {
                rating,
                comment,
                files
            }
        };
        await onSave(updatedReport);
        setIsSaving(false);
    };

    const ratingDescriptions = ["ضعيف جداً", "ضعيف", "متوسط", "جيد", "ممتاز"];

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="glass-panel w-full max-w-lg p-6 animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
                <input type="file" multiple ref={fileInputRef} className="hidden" onChange={handleFileChange} />

                <div className="flex justify-between items-center border-b border-white/10 pb-4 mb-6">
                    <div>
                        <h2 className="text-xl font-bold text-white">تقييم تقرير: {report.id}</h2>
                        <p className="text-sm text-slate-400">{report.employeeName} - {report.type}</p>
                    </div>
                    <button type="button" onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                        <X size={24} />
                    </button>
                </div>

                <div className="space-y-6">
                    <div className="flex flex-col items-center p-4 bg-slate-800/30 rounded-2xl border border-white/5">
                        <div className="flex items-center gap-2 mb-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    onClick={() => setRating(star)}
                                    className={`transition-all duration-200 hover:scale-110 ${rating >= star ? 'text-yellow-400 fill-yellow-400' : 'text-slate-600'}`}
                                >
                                    <Star size={32} className={rating >= star ? 'fill-current' : ''} />
                                </button>
                            ))}
                        </div>
                        <p className={`text-lg font-bold transition-colors ${rating > 0 ? 'text-yellow-400' : 'text-slate-500'}`}>
                            {rating > 0 ? ratingDescriptions[rating - 1] : "اختر تقييماً"}
                        </p>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-slate-300 mb-2 block">التعليقات والتوصيات</label>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="أضف تعليقك هنا..."
                            rows={4}
                            className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-3 px-4 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                        />
                    </div>

                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <label className="text-sm font-medium text-slate-300">المرفقات</label>
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="text-xs flex items-center gap-1 text-primary hover:text-primary/80 transition-colors"
                            >
                                <Upload size={14} />
                                <span>إرفاق ملفات</span>
                            </button>
                        </div>
                        <div className="space-y-2 max-h-32 overflow-y-auto p-2 bg-slate-800/30 rounded-xl border border-white/5 min-h-[60px]">
                            {files.length > 0 ? files.map(f => (
                                <div key={f.id} className="flex items-center justify-between bg-slate-700/50 p-2 rounded-lg text-xs group">
                                    <div className="flex items-center gap-2 text-slate-300">
                                        <FileText size={14} className="text-blue-400" />
                                        <span className="truncate max-w-[200px]">{f.fileName}</span>
                                    </div>
                                    <button onClick={() => removeFile(f.id)} className="text-slate-500 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100">
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            )) : <p className="text-xs text-slate-500 text-center py-4">لا توجد مرفقات.</p>}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-3 pt-6 border-t border-white/10 mt-6">
                    <button
                        onClick={onClose}
                        disabled={isSaving}
                        className="px-4 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                    >
                        إغلاق
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="px-6 py-2 rounded-lg bg-gradient-to-r from-primary to-purple-600 text-white font-medium hover:shadow-lg hover:shadow-primary/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {isSaving && <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                        <span>حفظ التقييم</span>
                    </button>
                </div>
            </div>
        </div>
    )
};

export default ModernEvaluationModal;
